import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { transcriptionAPI } from '../../services/api';
import {
  getTranscriptStatusLabel,
  normalizeTranscriptStatus,
  TRANSCRIPT_STATUS,
} from '../../utils/transcriptCertification';
import { formatDate } from '../../utils/transcriptionMeta';
import { getLanguageLabel } from '../../utils/languages';
import { toast } from 'sonner';

const COLUMN_DEFS = [
  { key: 'draft_id', label: 'Draft ID' },
  { key: 'case_session', label: 'Case / Session' },
  { key: 'language', label: 'Language' },
  { key: 'generated', label: 'Generation Date' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

const STATUS_OPTIONS = [
  { id: 'all', label: 'All Statuses' },
  { id: TRANSCRIPT_STATUS.DRAFT, label: 'Draft' },
  { id: TRANSCRIPT_STATUS.CERTIFIED, label: 'Certified' },
  { id: TRANSCRIPT_STATUS.SUPERSEDED, label: 'Superseded' },
];

const STATUS_PILL = {
  [TRANSCRIPT_STATUS.DRAFT]: 'bg-amber-50 text-amber-800 border-amber-200',
  [TRANSCRIPT_STATUS.CERTIFIED]: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  [TRANSCRIPT_STATUS.SUPERSEDED]: 'bg-gray-100 text-gray-600 border-gray-200',
};

const mapInboxRow = (t) => ({
  id: t.id || t.transcription_id,
  draft_id: t.transcription_id || t.id,
  title: t.title || t.filename || t.case_reference || 'Transcript',
  case_reference: t.case_reference || '—',
  session_id: t.audio_asset_id ? String(t.audio_asset_id).slice(0, 8).toUpperCase() : '—',
  language: t.language || 'English',
  generated_at: t.created_at,
  transcript_status: normalizeTranscriptStatus(t),
});

const AdminCertification = () => {
  const { user } = useAuth();
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [language, setLanguage] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));

  useEffect(() => {
    if (!user?.tenant_id) {
      setAllRows([]);
      return undefined;
    }
    let cancelled = false;
    transcriptionAPI.getTranscriptions({ per_page: 200 })
      .then((resp) => {
        if (cancelled) return;
        const rows = resp.data?.data || [];
        setAllRows((Array.isArray(rows) ? rows : []).map(mapInboxRow));
      })
      .catch(() => {
        if (!cancelled) setAllRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.tenant_id]);

  const languageOptions = useMemo(() => {
    const set = new Set(allRows.map((r) => r.language).filter(Boolean));
    return Array.from(set).sort();
  }, [allRows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((row) => {
      const matchesStatus = status === 'all' || row.transcript_status === status;
      const matchesLanguage = language === 'all' || row.language === language;
      const matchesQuery = !q
        || row.draft_id.toLowerCase().includes(q)
        || row.title.toLowerCase().includes(q)
        || row.case_reference.toLowerCase().includes(q)
        || row.session_id.toLowerCase().includes(q);
      let matchesDate = true;
      if (dateFrom || dateTo) {
        const d = row.generated_at ? new Date(row.generated_at) : null;
        if (!d || Number.isNaN(d.getTime())) {
          matchesDate = false;
        } else {
          if (dateFrom && d < new Date(`${dateFrom}T00:00:00`)) matchesDate = false;
          if (dateTo && d > new Date(`${dateTo}T23:59:59`)) matchesDate = false;
        }
      }
      return matchesStatus && matchesLanguage && matchesQuery && matchesDate;
    });
  }, [allRows, query, status, language, dateFrom, dateTo]);

  const awaitingReview = allRows.filter((r) => r.transcript_status === TRANSCRIPT_STATUS.DRAFT).length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);
  const show = (key) => visibleKeys.includes(key);
  const activeFilterCount = [
    status !== 'all',
    language !== 'all',
  ].filter(Boolean).length;

  const inputCls = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const searchCls = `${inputCls} w-full`;
  const dateCls = `${inputCls} w-[150px] shrink-0`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-20 hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const openDetail = (id) => navigate(`/admin/transcriptions/${id}`);

  const clearFilters = () => {
    setStatus('all');
    setLanguage('all');
    setPage(1);
  };

  const exportCsv = () => {
    const headers = ['Draft ID', 'Title', 'Case', 'Session', 'Language', 'Generated', 'Status'];
    const lines = filtered.map((row) =>
      [
        row.draft_id,
        row.title,
        row.case_reference,
        row.session_id,
        row.language,
        row.generated_at || '',
        getTranscriptStatusLabel(row.transcript_status),
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certification-inbox.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'certification-inbox.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  return (
    <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative w-full max-w-sm min-w-[180px] shrink">
            <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search draft, case, or session..."
              className={`${searchCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className={dropdownPanel}>
                <div className={dropdownInner}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                    <select
                      value={status}
                      onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                      className={`${inputCls} w-full`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Language</label>
                    <select
                      value={language}
                      onChange={(e) => { setLanguage(e.target.value); setPage(1); }}
                      className={`${inputCls} w-full`}
                    >
                      <option value="all">All Languages</option>
                      {languageOptions.map((l) => (
                        <option key={l} value={l}>{getLanguageLabel(l) || l}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Column visibility">
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
              <div className={dropdownPanel}>
                <div className={`w-52 py-2 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  {COLUMN_DEFS.map((col) => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                        dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleKeys.includes(col.key)}
                        onChange={() => setVisibleKeys((keys) => (
                          keys.includes(col.key) ? keys.filter((k) => k !== col.key) : [...keys, col.key]
                        ))}
                        className="rounded border-gray-300 text-matdash-primary"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className={dropdownPanel}>
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          Transcript inbox · {filtered.length} record{filtered.length === 1 ? '' : 's'}
          {awaitingReview > 0 ? ` · ${awaitingReview} draft${awaitingReview === 1 ? '' : 's'} awaiting review` : ''}
        </div>

        <div className="overflow-x-auto">
          <table className="md-table min-w-full">
            <thead>
              <tr>
                {show('draft_id') && <th>Draft ID</th>}
                {show('case_session') && <th>Case / Session</th>}
                {show('language') && <th>Language</th>}
                {show('generated') && <th>Generation Date</th>}
                {show('status') && <th>Status</th>}
                {show('actions') && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                    No transcripts match your filters.
                  </td>
                </tr>
              )}
              {pageRows.map((row) => {
                const pill = STATUS_PILL[row.transcript_status] || STATUS_PILL[TRANSCRIPT_STATUS.DRAFT];
                return (
                  <tr
                    key={row.id}
                    className={`cursor-pointer transition-colors ${dark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                    onClick={() => openDetail(row.id)}
                  >
                    {show('draft_id') && (
                      <td className="font-semibold whitespace-nowrap">
                        <div>{row.draft_id}</div>
                        <div className="text-xs font-normal text-matdash-muted truncate max-w-[220px]">{row.title}</div>
                      </td>
                    )}
                    {show('case_session') && (
                      <td className="whitespace-nowrap">
                        <div className="font-medium">{row.case_reference}</div>
                        <div className="text-xs text-matdash-muted">{row.session_id}</div>
                      </td>
                    )}
                    {show('language') && (
                      <td className="whitespace-nowrap">{getLanguageLabel(row.language) || row.language}</td>
                    )}
                    {show('generated') && (
                      <td className="text-matdash-muted whitespace-nowrap">
                        {formatDate(row.generated_at) || '—'}
                      </td>
                    )}
                    {show('status') && (
                      <td className="whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${pill}`}>
                          {getTranscriptStatusLabel(row.transcript_status)}
                        </span>
                      </td>
                    )}
                    {show('actions') && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <Link
                          to={`/admin/transcriptions/${row.id}`}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
                            dark
                              ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                              : 'border-gray-200 text-matdash-primary hover:bg-matdash-primary-subtle'
                          }`}
                          title="Open transcript"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <p className="text-xs text-matdash-muted mb-0">
            Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
            <span className="text-xs text-matdash-muted px-1">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
          </div>
        </div>
    </div>
  );
};

export default AdminCertification;
